import { Component, inject, OnDestroy, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { GlobalService } from "../../serivces/global.service";
import { SVG_ICONS } from "../../models/icons";
import { SanitizerPipe } from "../../pipes/sanitizer.pipe";
import { ActivatedRoute, Router } from "@angular/router";
import { MasterService } from "@frontend/app/serivces/master.service";
import { Cls_StudentAttendance } from "@frontend/app/models/master";
import { ToastrService } from "@frontend/app/serivces/toastr.service";
import { DialogModule } from "@angular/cdk/dialog";
import { ManualAttendanceComponent } from "./manual-attendance/manual-attendance.component";
import { MatDialog } from "@angular/material/dialog";
import { FormsModule } from "@angular/forms";
import { SharedModule } from "@frontend/app/shared/shared.module";

@Component({
  selector: "app-attendance",
  imports: [CommonModule, SanitizerPipe, FormsModule, SharedModule],
  templateUrl: "./attendance.component.html",
  styleUrl: "./attendance.component.scss",
})
export class AttendanceComponent implements OnInit, OnDestroy {
  private globalService = inject(GlobalService);
  private masterService = inject(MasterService);
  private activatedRoute = inject(ActivatedRoute);
  private toasterService = inject(ToastrService);
  private dialog = inject(MatDialog);
  private router = inject(Router);

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe(params => {
      if (params["ClassID"]) {
        this.ClassID = params["ClassID"];
        this.getClassDetailsByID();
      }
    });
    setTimeout(() => {
      this.globalService.setIsShowFalse();
    }, 0);
  }

  //#region variable
  readonly SVG_ICONS = SVG_ICONS;
  selectedMode: "Automatic" | "Manual" = "Manual";
  studentList: any[] = [];
  ClassID = 0;
  presentCount = 0;
  absentCount = 0;
  lateCount = 0;
  pendingCount = 0;
  trainerList: any;
  classDetails: any;
  todayClassStartTime: string = "";
  todayClassEndTime: string = "";
  availableTimeSlots: string[] = [];
  todayClassStartDateTime: Date | null = null;
  searchText: string = "";
  uploadedImagePreview: string | ArrayBuffer | null = null;
  selectedStatus: "All" | "Present" | "Late" | "Absent" | "Pending" = "All";

  get filteredStudentList() {
    let list = this.studentList;

    if (this.selectedStatus !== "All") {
      if (this.selectedStatus === "Present") {
        list = list.filter((s: any) => (s.attendanceStatus === "Present" || s.attendanceStatus === "Attend Late") && !s.isLate);
      } else if (this.selectedStatus === "Late") {
        list = list.filter((s: any) => s.isLate);
      } else if (this.selectedStatus === "Absent") {
        list = list.filter((s: any) => s.attendanceStatus === "Absent");
      } else if (this.selectedStatus === "Pending") {
        list = list.filter((s: any) => s.attendanceStatus === "Not Marked");
      }
    }

    if (!this.searchText) return list;
    const lowerSearch = this.searchText.toLowerCase();
    return list.filter((student: any) => {
      const fullName = `${student.FirstName || ""} ${student.MiddleName || ""} ${student.LastName || ""}`.toLowerCase();
      const studentId = student.StudentID ? student.StudentID.toString() : "";
      return fullName.includes(lowerSearch) || studentId.includes(lowerSearch);
    });
  }
  //#endregion

  selectMode(mode: "Automatic" | "Manual") {
    this.selectedMode = mode;
  }

  filterByStatus(status: "All" | "Present" | "Late" | "Absent" | "Pending") {
    this.selectedStatus = status;
  }

  onManualAttendance() {
    this.dialog.open(ManualAttendanceComponent, {
      width: "60%",
      data: {
        studentList: this.studentList,
        ClassID: this.classDetails.ClassID,
        CenterID: this.classDetails.CenterID,
        TrainerID: this.trainerList?.[0]?.TrainerID || 0,
      },
    });
  }

  getClassDetailsByID() {
    this.masterService.getClassDetailsByID(this.ClassID).subscribe((res: any) => {
      this.classDetails = res.data;
      this.setTodayClassSchedule();

      this.studentList = this.classDetails.Students.map((student: any) => ({
        ...student,
        age: this.getAge(student.DOB),
        isLate: false,
        lateTime: "",
        AttendanceID: 0,
      }));
      this.trainerList = this.classDetails.Trainers.map((trainer: any) => ({
        ...trainer,
      }));
      this.getClassAttendance();
    });
  }

  setTodayClassSchedule() {
    if (!this.classDetails?.Days) return;
    const daysMap = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
    const todayIndex = new Date().getDay();
    const todayName = daysMap[todayIndex];
    const todaySchedule = this.classDetails.Days.find((d: any) => d.DayName === todayName && d.IsActive);

    if (todaySchedule) {
      if (todaySchedule.StartTime) {
        const dateObj = new Date(todaySchedule.StartTime);
        if (!isNaN(dateObj.getTime())) {
          const hours = dateObj.getHours().toString().padStart(2, "0");
          const minutes = dateObj.getMinutes().toString().padStart(2, "0");
          this.todayClassStartTime = `${hours}:${minutes}`;
          const now = new Date();
          this.todayClassStartDateTime = new Date(now.getFullYear(), now.getMonth(), now.getDate(), dateObj.getHours(), dateObj.getMinutes(), 0);
        }
      }
      if (todaySchedule.EndTime) {
        const dateObj = new Date(todaySchedule.EndTime);
        if (!isNaN(dateObj.getTime())) {
          const hours = dateObj.getHours().toString().padStart(2, "0");
          const minutes = dateObj.getMinutes().toString().padStart(2, "0");
          this.todayClassEndTime = `${hours}:${minutes}`;
        }
      }
    }
    this.generateTimeSlots();
  }

  generateTimeSlots() {
    this.availableTimeSlots = [];
    if (!this.todayClassStartTime || !this.todayClassEndTime) return;
    const [startHour, startMin] = this.todayClassStartTime.split(":").map(Number);
    const [endHour, endMin] = this.todayClassEndTime.split(":").map(Number);
    const startDate = new Date();
    startDate.setHours(startHour, startMin, 0, 0);
    const endDate = new Date();
    endDate.setHours(endHour, endMin, 0, 0);
    if (startDate > endDate) return;

    let current = new Date(startDate);
    while (current <= endDate) {
      const h = current.getHours().toString().padStart(2, "0");
      const m = current.getMinutes().toString().padStart(2, "0");
      this.availableTimeSlots.push(`${h}:${m}`);
      current.setMinutes(current.getMinutes() + 1);
    }
  }

  onLateStatusChange(student: any) {
    if (student.isLate) {
      student.lateTime = this.todayClassStartTime || new Date().toTimeString().substring(0, 5);
    } else {
      student.lateTime = "";
    }

    if (student.attendanceStatus === "Present" || student.attendanceStatus === "Attend Late") {
      this.updateAttendance(student, "Present");
    }
  }

  getStudentProfileImage(student: any): string {
    if (student.ProfilePicture) return student.ProfilePicture;
    if (student.Documents?.length > 0) {
      const img = student.Documents.find((d: any) => d.FileType === "IMAGE" && d.URL);
      if (img) return img.URL;
    }
    return "public/no-image.jpg";
  }

  getTrainerProfileImage(trainer: any): string {
    if (trainer.ProfilePicture) return trainer.ProfilePicture;
    if (trainer.Documents?.length > 0) {
      const img = trainer.Documents.find((d: any) => d.FileType === "IMAGE" && d.URL);
      if (img) return img.URL;
    }
    return "public/no-image.jpg";
  }

  getClassAttendance() {
    if (!this.studentList?.length) return;
    const todayDate = new Date().toISOString().split("T")[0];

    this.masterService.getStudentAttendence(this.ClassID, todayDate).subscribe((res: any) => {
      const attendanceRecords = res;
      this.studentList = this.studentList.map((student: any) => {
        const record = attendanceRecords.find((a: any) => a.StudentID === student.StudentID);
        student.isEditing = false;
        if (record) {
          student.AttendanceID = record.AttendanceID;
          student.attendanceStatus = record.Status || record.AttendanceStatus;
          student.isLate = student.attendanceStatus === "Attend Late" ? true : record.IsComeLate || false;
          student.LateByMinutes = record.LateByMinutes || 0;
          student.lateTime = record.ClassAttendTime ? new Date(record.ClassAttendTime).toTimeString().substring(0, 5) : "";
          student.hasTodayAttendance = true;
        } else {
          student.AttendanceID = 0;
          student.attendanceStatus = null;
          student.hasTodayAttendance = false;
          student.isLate = false;
          student.lateTime = "";
          student.LateByMinutes = 0;
        }
        return student;
      });
      // Sort: Present first
      this.studentList.sort((a: any, b: any) => {
        const isAPresent = a.attendanceStatus === "Present" || a.attendanceStatus === "Attend Late";
        const isBPresent = b.attendanceStatus === "Present" || b.attendanceStatus === "Attend Late";
        if (isAPresent && !isBPresent) return -1;
        if (!isAPresent && isBPresent) return 1;
        return 0;
      });
      this.updateCounts();
    });
  }

  updateCounts() {
    this.presentCount = this.studentList.filter((s: any) => (s.attendanceStatus === "Present" || s.attendanceStatus === "Attend Late") && !s.isLate).length;
    this.lateCount = this.studentList.filter((s: any) => s.isLate).length;
    this.absentCount = this.studentList.filter((s: any) => s.attendanceStatus === "Absent").length;
    this.pendingCount = this.studentList.filter((s: any) => s.attendanceStatus === "Not Marked").length;
  }

  // --- Edit Logic ---
  enableEdit(student: any) {
    student.isEditing = true;
  }

  cancelEdit(student: any) {
    student.isEditing = false;
  }

  onSaveAttendance(student: any, status: "Present" | "Absent") {
    if (student.hasTodayAttendance) {
      this.updateAttendance(student, status);
    } else {
      this.AddStudentAttendance(student, status);
    }
  }

  // ------------------

  getAge(dateString: string): number {
    const today = new Date();
    const birthDate = new Date(dateString);
    let age = today.getFullYear() - birthDate.getFullYear();
    if (today.getMonth() < birthDate.getMonth() || (today.getMonth() === birthDate.getMonth() && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  }

  hasAbsentStudents(): boolean {
    return this.studentList?.some((s: any) => s.attendanceStatus !== "Present") || false;
  }

  AddStudentAttendance(student: any, status: "Present" | "Absent") {
    const classAttendTime = this.todayClassStartDateTime || new Date();
    const UserID = sessionStorage.getItem("UserID");

    const payload = {
      StudentID: student.StudentID,
      CenterID: this.classDetails.CenterID,
      TrainerID: UserID || 0,
      ClassID: this.classDetails.ClassID,
      AttendanceDate: this.toISTISOString(new Date()),
      SessionStart: this.toISTISOString(classAttendTime),
      SessionEnd: this.toISTISOString(new Date()),
      ModeOfEntry: this.selectedMode,
      IsPresent: status === "Present",
      Status: status,
      IsComeLate: status === "Absent" ? false : student.isLate || false,
      ClassAttendTime: this.toISTISOString(classAttendTime),
      LateByMinutes: student.LateByMinutes || 0,
      Remarks: "",
      Documents: [],
    };
    console.log(payload)

    this.masterService.addStudentAttendance({ records: [payload] }).subscribe(() => {
      this.toasterService.success("Attendance marked");
      this.getClassAttendance();
    });
  }

  updateAttendance(student: any, status: "Present" | "Absent") {
    if (!student.AttendanceID) {
      this.toasterService.error("Attendance ID missing");
      return;
    }

    const payload = {
      Status: status,
      IsPresent: status === "Present",
      IsComeLate: status === "Absent" ? false : student.isLate || false,
      ClassAttendTime: student.lateTime ? this.toISTISOString(new Date(new Date().toDateString() + " " + student.lateTime)) : null,
      LateByMinutes: student.LateByMinutes || 0,
      Remarks: "",
    };

    this.masterService.updateStudentAttendance(payload, student.AttendanceID).subscribe(() => {
      this.toasterService.success("Attendance updated");

      student.attendanceStatus = status;
      if (status === "Absent") {
        student.isLate = false;
        student.lateTime = "";
      }
      student.isEditing = false;
      student.hasTodayAttendance = true;

      this.updateCounts();
    });
  }

  finalizeAttendance(student: any, status: string) {
    student.attendanceStatus = status;
    student.hasTodayAttendance = true;
    student.isEditing = false; // Hide buttons, show Edit icon
    this.updateCounts();
  }

  AddMarkAllAsPresent() {
    if (!this.studentList?.length) {
      this.toasterService.error("No students found");
      return;
    }
    const startTime = this.todayClassStartDateTime ? this.toISTISOString(this.todayClassStartDateTime) : this.toISTISOString(new Date());

    const attendanceRecords = this.studentList.map((student: any) => ({
      StudentID: Number(student.StudentID),
      CenterID: Number(this.classDetails.CenterID),
      TrainerID: Number(this.trainerList[0].TrainerID || 0),
      ClassID: Number(this.classDetails.ClassID),
      AttendanceDate: this.toISTISOString(new Date()),
      SessionStart: startTime,
      SessionEnd: new Date(new Date().getTime() + 3600000).toISOString(),
      ModeOfEntry: this.selectedMode,
      IsPresent: true,
      Status: "Present",
      IsComeLate: false,
      LateByMinutes: 0,
      Remarks: "",
      IsMediaUpload: false,
      Documents: [],
    }));

    this.masterService.addStudentAttendance({ records: attendanceRecords }).subscribe(() => {
      this.toasterService.success("All students marked present");
      this.getClassAttendance(); // Reload to get IDs
    });
  }

  onImgError(event: Event) {
    (event.target as HTMLImageElement).src = "public/no-image.jpg";
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const result = e.target.result;
        this.uploadedImagePreview = result;
        const base64String = result.split(",")[1];
        if (!base64String) {
          this.toasterService.error("Invalid image.");
          return;
        }
        const payload = {
          ClassID: Number(this.ClassID),
          CenterID: Number(this.classDetails?.CenterID || 0),
          Description: "Class Image Upload",
          AttendanceDate: this.toISTISOString(new Date()),
          Documents: [{ FileName: file.name, MimeType: file.type || "image/png", Base64: "data:image/jpeg;base64," + base64String }],
        };
        this.masterService.uploadClassDocument(payload).subscribe({
          next: () => this.toasterService.success("Image uploaded"),
          error: () => this.toasterService.error("Upload failed"),
        });
      };
      reader.readAsDataURL(file);
    }
  }

  onBack(ClassID: number) {
    this.router.navigate(["class-list/class-details"], { queryParams: { ClassID: ClassID } });
  }

  toISTISOString(date: Date): string {
    const istOffset = 330 * 60000;
    const istDate = new Date(date.getTime() + istOffset);
    return istDate.toISOString().slice(0, -1) + "+05:30";
  }

  ngOnDestroy(): void {
    this.globalService.setIsShowTrue();
  }
}
